import { CurrencyPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core'
import { Store, select } from '@ngrx/store'
import { Cost, CreditBalance, CreditNote, CreditPool, Deal, DealPartyE, DealViewRaw, ForexData, ForexRange, INVOICE_ACTUALIZED, INVOICE_APPLIED, INVOICE_APPROVED, INVOICE_PAID, INVOICE_PARTIALLY_PAID, INVOICE_SCHEDULED, LocationObject, Measure, Note, User, VendorCredit } from '@tradecafe/types/core'
import { DeepPartial, DeepReadonly, isDealConfirmedBy, printFormula } from '@tradecafe/types/utils'
import { resolveHsCode } from 'src/services/data/hs-code.utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { isEqual, range, round, sortBy } from 'lodash-es'
import { Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs'
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take, tap } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { FormulaPricingApiService, type FormulaPricingPreviewFormula } from 'src/api/formula-pricing'
import { GenericOption, loadAccounts, selectAccountEntities, selectBuyersOptions, selectSuppliersOptions } from 'src/app/store/accounts'
import { loadConsignees } from 'src/app/store/consignees'
import { loadCountries, selectAllCountries } from 'src/app/store/countries'
import { loadCurrencies, selectAllCurrencies } from 'src/app/store/currencies'
import { loadItemTypes, selectAllItemTypes } from 'src/app/store/item-types'
import { selectAllLocations, selectLocationEntities } from 'src/app/store/locations'
import { loadMeasures, selectAllMeasures, selectMeasureEntities } from 'src/app/store/measures'
import { loadPackageTypes, selectAllPackageTypes } from 'src/app/store/package-types'
import { loadPaymentReferences } from 'src/app/store/payment-references'
import { loadPricingTerms, selectAllPricingTerms, selectPricingTermEntities } from 'src/app/store/pricing-terms'
import { loadProductCategories } from 'src/app/store/product-categories'
import { loadProductTypes } from 'src/app/store/product-types'
import { loadProducts, selectProductEntities } from 'src/app/store/products'
import { USER_FIELDS, loadUsers, selectCoordinatorsOptions, selectTradersOptions, selectUserEntities } from 'src/app/store/users'
import { loadWeightTypes, selectAllWeightTypes } from 'src/app/store/weight-types'
import { loadWrappingTypes, selectAllWrappingTypes } from 'src/app/store/wrapping-types'
import { formulaPricingLineChip, formulaPricingLineEntry, formulaPricingLineView, isUsdaProposalOnDealEnabled, type FormulaPricing, type FormulaPricingLineChip, type FormulaPricingLineView, type FormulaPricingSideKey } from 'src/components/deals-list/formula-pricing'
import { usdaLivePreview$, usdaPreviewChip } from 'src/components/usda-proposal-panel'
import { DocsCountryConfirmationDialogService } from 'src/components/docs-country-confirmation-dialog'

import { NoteFormService } from 'src/components/notes/note-form/note-form.service'
import { MeasurePipe } from 'src/filters/measure.pipe'
import { establishmentAddressOptions, invoiceAddressOptions } from 'src/pages/admin/logistics/shipping-details-page/deal-products-list/deal-product-form/deal-product-form.component'
import { getPartyUsers, isBwiInventory } from 'src/services/data/accounts.service'
import { archivedContactsWarning } from 'src/shared/utils/archived-contacts-warning'
import { getDefaultGeo } from 'src/services/data/users.service'

import { CreditPoolService } from 'src/services/data/credit-pool.service'
import { DealFormCalculatorService } from 'src/services/data/deal-form-calculator.service'
import { canEditDealFormField } from 'src/services/data/deal-view-permissions.service'
import { MeasuresService } from 'src/services/data/measures.service'
import { SPECIAL_INSTRUCTIONS } from 'src/services/data/notes.service'
import { isManagerOrSuperuser } from 'src/services/data/users.service'
import { selectOptions, waitNotEmpty } from 'src/services/data/utils'
import { dayjs } from 'src/services/dayjs'
import { replayForm } from 'src/shared/utils/replay-form'
import { setupItemSpecPackageAutoPopulation } from 'src/services/data/item-spec-package.utils'
import { isUsdaPriceSource } from 'src/shared/formula-form'
import { DealDocumentsService } from '../../../deals/deal-documents/deal-documents.service'
import { ChangePartyFormService } from '../change-party-form/change-party-form.service'
import { DealAutocopyFormService } from '../deal-autocopy-form/deal-autocopy-form.service'
import { isLockedSel } from '../deal-form-page.decorator'
import { CostsFormGroup, DealDetailsFormGroup, DealDetailsFormValue, DealFormGroup, DealProductFormGroup, DealProductFormValueBase, DealProductsFormGroup } from '../deal-form.schema'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { DealFormService } from '../deal-form.service'
import { canAddProductSel, canRemoveProductSel, hasBuyerInstructionsSel, hasSupplierInstructionsSel, isBuyerPortalVisibleSel, isSupplierPortalVisibleSel } from './deal-details.decorator'


/** Formula links of one product line, with their USDA proposal chips (WA-22777) */
interface FormulaBarState {
  canSetSupplier: boolean
  canSetBuyer: boolean
  supplierFormula: string
  buyerFormula: string
  supplierProposal: FormulaPricingLineChip | null
  buyerProposal: FormulaPricingLineChip | null
}

@Component({
  selector: 'tc-deal-details-v2',
  templateUrl: './deal-details.component.html',
  styleUrls: ['./deal-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DealDetailsComponent extends OnDestroyMixin implements OnInit {
  private Measures = inject(MeasuresService)
  private NoteForm = inject(NoteFormService)
  private DealFormCalculator = inject(DealFormCalculatorService)
  private CreditPoolSvc = inject(CreditPoolService)
  private AuthApi = inject(AuthApiService)
  private FormulaPricingApi = inject(FormulaPricingApiService)
  private ChangePartyForm = inject(ChangePartyFormService)
  private DealAutocopyForm = inject(DealAutocopyFormService)
  private DealForm = inject(DealFormService)
  private measure = inject(MeasurePipe)
  private currency = inject(CurrencyPipe)
  private DealDocuments = inject(DealDocumentsService)
  private store = inject(Store)
  private docsCountryDialog = inject(DocsCountryConfirmationDialogService)


  private readonly toaster = inject(ToasterService)
  protected readonly DealPartyE = DealPartyE

  // "weight" options
  readonly weights = range(5000, 26501, 500)
  // minimum shipping date. allow users from GMT+N to create "yesterday" deals at their midnight till N:00 AM
  readonly TODAY = dayjs().utc().startOf('day').unix()
  protected readonly isManager = isManagerOrSuperuser(this.AuthApi.currentUser)

  @Input() mode: 'finance' | 'products'
  @Input() dealId: string
  @Input() dealViewRaw$: Observable<DeepReadonly<DealViewRaw>>
  dealRaw$: Observable<DeepReadonly<Deal>> // pristine deal. doesn't include any form changes
  creditNotes$: Observable<DeepReadonly<CreditNote[]>>
  vendorCredits$: Observable<DeepReadonly<VendorCredit[]>>

  protected isDealConfirmedByBrokerageCustomer$: Observable<boolean>

  @Input() costs$: Observable<DeepReadonly<DeepPartial<Cost>[]>>

  isLocked$: Observable<boolean>
  canAddProduct$: Observable<boolean>
  canRemoveProduct$: Observable<boolean>
  isSupplierPortalVisible$: Observable<boolean>
  isBuyerPortalVisible$: Observable<boolean>

  hasSupplierInstructions$: Observable<DeepReadonly<Note>>
  hasBuyerInstructions$: Observable<DeepReadonly<Note>>
  fxRates$: Observable<ForexData>
  fxRatesAskRange$: Observable<ForexRange>
  primaryCostsVisible  = false

  @Input() dealForm: DealFormGroup
  detailsForm: DealDetailsFormGroup
  productsForm: DealProductsFormGroup
  costsForm: CostsFormGroup

  @Output() updateCost = new EventEmitter<DeepReadonly<Partial<Cost>>>()
  @Output() removeCost = new EventEmitter<DeepReadonly<Partial<Cost>>>()

  // ref data
  countries$ = this.store.pipe(select(selectAllCountries), waitNotEmpty())
  currencies$ = this.store.pipe(select(selectAllCurrencies), waitNotEmpty())
  itemTypes$ = this.store.pipe(select(selectAllItemTypes), waitNotEmpty())
  locations$ = this.store.pipe(select(selectAllLocations), waitNotEmpty())
  measures$ = this.store.pipe(select(selectAllMeasures), waitNotEmpty())
  private measuresById$ = this.store.pipe(select(selectMeasureEntities), waitNotEmpty())
  private locationsById$ = this.store.pipe(select(selectLocationEntities), waitNotEmpty())
  private pricingTermsById$ = this.store.pipe(select(selectPricingTermEntities), waitNotEmpty())
  packageTypes$ = this.store.pipe(select(selectAllPackageTypes), waitNotEmpty())
  pricingTerms$ = this.store.pipe(select(selectAllPricingTerms), waitNotEmpty())
  weightTypes$ = this.store.pipe(select(selectAllWeightTypes), waitNotEmpty())
  wrappings$ = this.store.pipe(select(selectAllWrappingTypes), waitNotEmpty())
  // user options
  private users$ = this.store.pipe(select(selectUserEntities), waitNotEmpty())
  buyerTraders$: Observable<GenericOption[]>
  buyerUsers$: Observable<DeepReadonly<User[]>>
  coordinators$: Observable<GenericOption[]>
  supplierTraders$: Observable<GenericOption[]>
  supplierUsers$: Observable<DeepReadonly<User[]>>
  // account options
  private accounts$ = this.store.pipe(select(selectAccountEntities), waitNotEmpty())
  buyers$: Observable<DeepReadonly<GenericOption[]>>
  suppliers$: Observable<DeepReadonly<GenericOption[]>>
  supplierChanged$ = new Subject<string>()
  buyerChanged$ = new Subject<string>()
  supplierTraderChanged$ = new Subject<string>()
  buyerTraderChanged$ = new Subject<string>()

  buyerCurrency$: Observable<string>
  docsCountryIssues$: Observable<string>
  supplierContactsArchivedWarning$: Observable<string>
  buyerContactsArchivedWarning$: Observable<string>

  // dynamic
  balance$ = new ReplaySubject<CreditBalance>(1)
  creditPool$ = new ReplaySubject<CreditPool>(1)

  ngOnInit() {
    this.detailsForm = this.dealForm.controls.details
    this.productsForm = this.dealForm.controls.products
    this.costsForm = this.dealForm.controls.costs
    this.docsCountryIssues$ = this.DealDocuments.hasDocsCountryIssues$(
      this.dealForm,
      this.store.pipe(select(selectProductEntities), waitNotEmpty()))
    this.buyerCurrency$ = replayForm(this.detailsForm.controls.buyerCurrencyCode)

    this.buyers$ = selectOptions(this.store, selectBuyersOptions, this.detailsForm.controls.buyerId)
    this.suppliers$ = selectOptions(this.store, selectSuppliersOptions, this.detailsForm.controls.supplierId)
    this.coordinators$ = selectOptions(this.store, selectCoordinatorsOptions, this.detailsForm.controls.logisticsUserId)
    this.buyerTraders$ = selectOptions(this.store, selectTradersOptions, this.detailsForm.controls.buyerTraderId)
    this.supplierTraders$ = selectOptions(this.store, selectTradersOptions, this.detailsForm.controls.supplierTraderId)

    this.dealRaw$ = this.dealViewRaw$.pipe(map(dto => dto.deal))
    this.creditNotes$ = this.dealViewRaw$.pipe(map(dto => dto.credit_notes))
    this.vendorCredits$ = this.dealViewRaw$.pipe(map(dto => dto.vendor_credits))
    this.isLocked$ = isLockedSel(this.dealRaw$)
    this.canAddProduct$ = canAddProductSel(this.dealViewRaw$, this.AuthApi.currentUser)
    this.canRemoveProduct$ = canRemoveProductSel(this.dealViewRaw$, this.productsForm, this.AuthApi.currentUser)
    this.isSupplierPortalVisible$ = isSupplierPortalVisibleSel(this.dealRaw$)
    this.isBuyerPortalVisible$ = isBuyerPortalVisibleSel(this.dealRaw$)
    this.hasSupplierInstructions$ = hasSupplierInstructionsSel(this.dealViewRaw$)
    this.hasBuyerInstructions$ = hasBuyerInstructionsSel(this.dealViewRaw$)
    this.fxRates$ = replayForm(this.detailsForm.controls.fxRates)
    this.fxRatesAskRange$ = replayForm(this.detailsForm.controls.fxRatesAskRange)
    this.supplierUsers$ = combineLatest([this.users$, replayForm(this.detailsForm.controls.supplierId), replayForm(this.detailsForm.controls.supplierUserIds)]).pipe(
      map(([users, account, selectedIds]) => getPartyUsers(users, account).filter(user => !user.archived || selectedIds?.includes(user.user_id)))
    )
    this.buyerUsers$ = combineLatest([this.users$, replayForm(this.detailsForm.controls.buyerId), replayForm(this.detailsForm.controls.buyerUserIds)]).pipe(
      map(([users, account, selectedIds]) => getPartyUsers(users, account).filter(user => !user.archived || selectedIds?.includes(user.user_id)))
    )
    this.supplierContactsArchivedWarning$ = combineLatest([
      this.users$, replayForm(this.detailsForm.controls.supplierId), replayForm(this.detailsForm.controls.supplierUserIds),
    ]).pipe(map(([users, account, selectedIds]) => archivedContactsWarning(getPartyUsers(users, account), selectedIds)))
    this.buyerContactsArchivedWarning$ = combineLatest([
      this.users$, replayForm(this.detailsForm.controls.buyerId), replayForm(this.detailsForm.controls.buyerUserIds),
    ]).pipe(map(([users, account, selectedIds]) => archivedContactsWarning(getPartyUsers(users, account), selectedIds)))

    this.store.dispatch(loadAccounts({}))
    this.store.dispatch(loadConsignees())
    this.store.dispatch(loadCountries())
    this.store.dispatch(loadCurrencies({}))
    this.store.dispatch(loadItemTypes())
    this.store.dispatch(loadMeasures())
    this.store.dispatch(loadPackageTypes())
    this.store.dispatch(loadPaymentReferences({}))
    this.store.dispatch(loadPricingTerms({}))
    this.store.dispatch(loadProductCategories())
    this.store.dispatch(loadProducts())
    this.store.dispatch(loadProductTypes())
    this.store.dispatch(loadUsers({fields: USER_FIELDS.DROPDOWN}))
    this.store.dispatch(loadWeightTypes())
    this.store.dispatch(loadWrappingTypes())

    this.setupItemTypeAutoPopulation()

    this.refreshBuyerBalance(this.detailsForm.controls.buyerId.value)
    combineLatest([
      this.supplierChanged$.pipe(switchMap(accountId => this.onSupplierChange(accountId))),
      this.buyerChanged$.pipe(switchMap(accountId => this.onBuyerChange(accountId))),
      this.supplierTraderChanged$.pipe(switchMap(() => this.onSupplierTraderChange())),
      this.buyerTraderChanged$.pipe(switchMap(() => this.onBuyerTraderChange())),
    ]).pipe(untilComponentDestroyed(this)).subscribe()

    this.isDealConfirmedByBrokerageCustomer$ = this.dealRaw$.pipe(
      untilComponentDestroyed(this),
      map(deal => Boolean(isDealConfirmedBy(deal, DealPartyE.brokerage_customer)))
    )

    this.productsForm.controls.forEach(productForm => this.setupHsCodePopulation(productForm))

    this.dealRaw$.pipe(take(1)).subscribe((d) => {
      if (!d.attributes.estimate_id) return

      if (this.detailsForm.controls.supplierId.value) {
        this.supplierChanged$.next(this.detailsForm.controls.supplierId.value)
      }
      if (this.detailsForm.controls.buyerId.value) {
        this.buyerChanged$.next(this.detailsForm.controls.buyerId.value)
      }
    })
  }

  private setupHsCodePopulation(productForm: DealProductFormGroup) {
    combineLatest([
      this.accounts$,
      this.store.pipe(select(selectProductEntities), waitNotEmpty()),
      replayForm(this.detailsForm.controls.buyerId),
      replayForm(productForm.controls.productId),
      replayForm(productForm.controls.itemTypeId),
    ]).pipe(
      map(([accounts, products, buyerId, product_id, item_type_id]) => {
        const buyer = accounts[buyerId]
        if (!buyer || !product_id) return null
        return resolveHsCode(buyer, products[product_id], { product_id, item_type_id })
      }),
      distinctUntilChanged(),
      untilComponentDestroyed(this)
    ).subscribe(hsCode => {
      if (hsCode !== null) {
        const hsCodeControl = productForm.controls.hsCode
        hsCodeControl.setValue(hsCode || '', { emitEvent: false })
        hsCodeControl.markAsDirty()
      }
    })
  }

  invoiceAddressOptions$(productForm: DealProductFormGroup) {
    return invoiceAddressOptions(this.detailsForm, productForm)
  }

  establishmentAddressOptions$(productForm: DealProductFormGroup) {
    return establishmentAddressOptions(this.detailsForm, productForm)
  }

  supplierIndPrice$(productForm: DealProductFormGroup) {
    return combineLatest([
      replayForm(this.detailsForm),
      replayForm(productForm),
    ]).pipe(map(([deal, product]) =>
      product.supplierEstPrice && deal.supplierCurrencyCode && product.supplierMeasureId
        ? this.measure.transform(
            this.currency.transform(product.supplierEstPrice, deal.supplierCurrencyCode, 'symbol-narrow', '1.4'/* , 'no_TH' */),
            product.supplierMeasureId)
        : ''))
  }

  buyerIndPrice$(productForm: DealProductFormGroup) {
    return combineLatest([
      replayForm(this.detailsForm),
      replayForm(productForm),
    ]).pipe(map(([deal, product]) =>
      product.buyerEstPrice && deal.buyerCurrencyCode && product.buyerMeasureId
        ? this.measure.transform(
            this.currency.transform(product.buyerEstPrice, deal.buyerCurrencyCode, 'symbol-narrow', '1.4'/* , 'no_TH' */),
            product.buyerMeasureId)
        : ''))
  }

  supplierIndWeight$(productForm: DealProductFormGroup) {
    return replayForm(productForm).pipe(
      map(({ supplierEstWeight, supplierMeasureId }) =>
        supplierEstWeight || supplierEstWeight === 0 ? this.measure.transform(supplierEstWeight, supplierMeasureId) : ''),
      distinctUntilChanged())
  }

  buyerIndWeight$(productForm: DealProductFormGroup) {
    return replayForm(productForm).pipe(
      map(({ buyerEstWeight, buyerMeasureId }) =>
        buyerEstWeight || buyerEstWeight === 0 ? this.measure.transform(buyerEstWeight, buyerMeasureId) : ''),
      distinctUntilChanged())
  }

  /** the supplier and the buyer column both render a line's formula bar: one shared stream per line */
  private formulaBars = new WeakMap<DealProductFormGroup, { index: number, state$: Observable<FormulaBarState | undefined> }>()

  protected formulaBarState$ = (index: number): Observable<FormulaBarState | undefined> => {
    if (!this.dealForm) return of(undefined)
    const productForm = this.dealForm.controls.products.at(index)
    const cached = this.formulaBars.get(productForm)
    if (cached?.index === index) return cached.state$
    const bar$ = combineLatest([
      this.dealViewRaw$,
      this.AuthApi.currentUser$,
      replayForm(this.dealForm.controls.details),
      replayForm(productForm),
      combineLatest([this.locationsById$, this.measuresById$, this.pricingTermsById$]),
    ]).pipe(map(([dv, currentUser, { supplierCurrencyCode, buyerCurrencyCode }, pf, [locations, measures, pricingTerms]]) => {
      const canSetSupplier = canEditDealFormField(dv, 'supplierEstPrice', currentUser) && !pf.supplierFormula
      const canSetBuyer = canEditDealFormField(dv, 'buyerEstPrice', currentUser) && !pf.buyerFormula
      const supplierFormula = printFormula(pf.supplierFormula, supplierCurrencyCode, pf.supplierMeasureId, pf.supplierIncotermId, {locations, measures, pricingTerms})
      const buyerFormula = printFormula(pf.buyerFormula, buyerCurrencyCode, pf.buyerMeasureId, pf.buyerIncotermId, {locations, measures, pricingTerms})
      return canSetSupplier || canSetBuyer || supplierFormula || buyerFormula
        ? { canSetSupplier, canSetBuyer, supplierFormula, buyerFormula }
        : undefined
    }))
    const state$ = combineLatest([
      bar$,
      this.proposalChip$(productForm, index, 'supplier'),
      this.proposalChip$(productForm, index, 'buyer'),
    ]).pipe(
      map(([bar, supplierProposal, buyerProposal]) => bar && { ...bar, supplierProposal, buyerProposal }),
      shareReplay({ bufferSize: 1, refCount: true }))
    this.formulaBars.set(productForm, { index, state$ })
    return state$
  }

  /**
   * WA-22777: USDA proposal chip next to a USDA formula, priced live like the formula dialog (not the last nightly
   * result), so the deal page and the dialog never disagree. Repriced when the formula on the form changes and
   * whenever the deal reloads (e.g. after a save). Null when the flag is off or the formula isn't USDA.
   */
  private proposalChip$(productForm: DealProductFormGroup, index: number, side: FormulaPricingSideKey): Observable<FormulaPricingLineChip | null> {
    if (!isUsdaProposalOnDealEnabled()) return of(null)
    const formula$ = replayForm(productForm).pipe(
      map(pf => (side === 'supplier' ? pf.supplierFormula : pf.buyerFormula) ?? null),
      distinctUntilChanged(isEqual))
    const deal$ = combineLatest([this.dealViewRaw$, replayForm(productForm)]).pipe(
      map(([dv, pf]) => ({
        dealId: dv?.deal?.deal_id,
        nightly: formulaPricingLineEntry(
          (dv?.deal as { formula_pricing?: FormulaPricing })?.formula_pricing,
          side,
          side === 'supplier' ? pf.offer?.offer_id : pf.bid?.bid_id),
        loaded: dv,
      })),
      distinctUntilChanged((a, b) => a.loaded === b.loaded && a.dealId === b.dealId && isEqual(a.nightly, b.nightly)))
    const currency$ = replayForm(this.dealForm.controls.details).pipe(
      map(d => side === 'supplier' ? d.supplierCurrencyCode : d.buyerCurrencyCode),
      distinctUntilChanged())

    return combineLatest([deal$, currency$]).pipe(switchMap(([{ dealId, nightly }, currency]) => {
      const priceUnit = (view: FormulaPricingLineView) =>
        view.proposed ? this.measure.transform(view.currency ?? currency ?? '', view.unit) : ''
      // a deal that isn't saved yet has nothing to price against: show the stored state (none)
      if (!dealId) {
        return formula$.pipe(map(formula => {
          if (!formula || !isUsdaPriceSource(formula.price_source)) return null
          const view = formulaPricingLineView(nightly, formula)
          return formulaPricingLineChip(view, priceUnit(view))
        }))
      }
      const state$ = usdaLivePreview$({
        formula$,
        preview: formula => this.FormulaPricingApi.preview(dealId, {
          side,
          product_index: index,
          formula: formula as FormulaPricingPreviewFormula,
        }),
        nightly,
        visible: true,
      })
      return combineLatest([state$, formula$]).pipe(map(([state, formula]) => usdaPreviewChip(state, formula, priceUnit)))
    }), distinctUntilChanged(isEqual))
  }

  protected showChangeFormula(party: DealPartyE.buyer | DealPartyE.supplier, productIndex: number) {
    this.dealViewRaw$.pipe(take(1), switchMap(dv =>
      this.ChangePartyForm.showChangeFormula(dv, this.dealForm, productIndex, party)),
    ).subscribe()
  }

  addProduct() {
    combineLatest([this.accounts$, this.dealViewRaw$]).pipe(take(1)).subscribe(([accounts, dv]) => {
      const firstProductForm = this.productsForm.at(0).getRawValue()
      const detailsForm = this.detailsForm.getRawValue()

      const supplier = accounts[detailsForm.supplierId].attributes.pricing
      const buyer = accounts[detailsForm.buyerId].attributes.pricing

      const productForm = this.DealForm.buildDealProductForm()
      productForm.patchValue({
        supplierMeasureId: firstProductForm.supplierMeasureId || undefined,
        supplierIncotermId: supplier?.incoterm || undefined,
        supplierIncotermLocationId: detailsForm.originLocationId || supplier?.tax_location || undefined,
        buyerMeasureId: firstProductForm.buyerMeasureId || undefined,
        buyerIncotermId: buyer?.incoterm || undefined,
        buyerIncotermLocationId: detailsForm.destLocationId || buyer?.tax_location || undefined,
      })
      this.DealForm.enableDisableProductForm(productForm, dv)
      this.productsForm.push(productForm)

      this.setupHsCodePopulation(productForm)
    })
  }

  private validateRemoveProduct(productIndex: number): boolean {
    let dealViewRaw: DeepReadonly<DealViewRaw>
    this.dealViewRaw$.pipe(take(1)).subscribe(dv => dealViewRaw = dv)
    if (!dealViewRaw?.invoices) {
      this.toaster.error('Something went wrong. Please reload the deal and try again.')
      return false
    }

    const primaryCost = this.dealForm.controls.costs.value.find(({cost}) => cost.type === 'primary' && cost.attributes?.product_index === productIndex)?.cost
    if (!primaryCost?.cost_id) return true

    const blockingInvoiceId = dealViewRaw.invoices.find(invoice =>
      invoice.type === 'payable' &&
      !invoice.void &&
      [
        INVOICE_ACTUALIZED, INVOICE_SCHEDULED, INVOICE_APPROVED,
        INVOICE_PAID, INVOICE_PARTIALLY_PAID, INVOICE_APPLIED,
      ].includes(invoice.status) &&
      invoice.attributes?.costs?.some(({cost_id}) => cost_id === primaryCost.cost_id))?.invoice_id
    if (blockingInvoiceId) {
      this.toaster.warning(`You cannot delete this product line because it is associated with invoice ${blockingInvoiceId}.`)
      return false
    }

    return true
  }

  removeProduct(product_index: number) {
    if (!this.validateRemoveProduct(product_index)) return

    this.productsForm.removeAt(product_index)
    // remove primary cost and adjust "product_index" in remaining costs
    const costsForm = this.dealForm.controls.costs
    costsForm.value.reduce((res, {cost}, index) => {
      if (cost.type === 'primary' && cost.attributes?.product_index === product_index) res.push(index)
      return res
    }, [] as number[]).sort().reverse().forEach(index => costsForm.removeAt(index))

    const primaryCosts = sortBy(costsForm.controls.filter(cf => cf.value.cost.type === 'primary'), 'value.cost.attributes.product_index')
    // here we expect product_indexes to be set and unique
    primaryCosts.slice(product_index).forEach((costForm) => {
      const cf = costForm.controls.cost
      cf.setValue({
        ...cf.value,
        attributes: {
          ...cf.value.attributes,
          product_index: cf.value.attributes.product_index - 1,
        },
      })
    })

    this.doCalculations().subscribe()
  }

  buyerConfirm() {
    this.detailsForm.patchValue({
      buyerConfirmedAt: this.detailsForm.getRawValue().buyerConfirmedAt
        ? 0 : dayjs().unix(),
      buyerConfirmedBy: this.AuthApi.currentUser.user_id,
    })
    // this.updateDealStatus()
  }

  sellerConfirm() {
    this.detailsForm.patchValue({
      supplierConfirmedAt: this.detailsForm.getRawValue().supplierConfirmedAt
        ? 0 : dayjs().unix(),
      supplierConfirmedBy: this.AuthApi.currentUser.user_id,
    })
    // this.updateDealStatus()
  }

  onSupplierTraderChange() {
    const supplierId = this.detailsForm.controls.supplierId.value
    if (!supplierId) return of(0)
    const traderId = this.detailsForm.controls.supplierTraderId.value
    return this.DealFormCalculator.refillPaymentTerms(supplierId, DealPartyE.supplier, traderId)
      .pipe(tap(details => this.dealForm.patchValue({ details })))
  }

  onBuyerTraderChange() {
    const buyerId = this.detailsForm.controls.buyerId.value
    if (!buyerId) return of(0)
    const traderId = this.detailsForm.controls.buyerTraderId.value
    return this.DealFormCalculator.refillPaymentTerms(buyerId, DealPartyE.buyer, traderId)
      .pipe(tap(details => this.dealForm.patchValue({ details })))
  }

  /**
   * Observe supplier change
   *
   * @param {*} newSupplierId
   */
  onSupplierChange(supplierId: string) {
    if (!supplierId) {
      this.DealForm.clearSupplierFields(this.detailsForm)
      return this.DealForm.refreshSecondaryCosts(this.dealForm)
    }

    const { buyerId } = this.detailsForm.getRawValue()
    this.suggestDealAutocopy({ buyerId, supplierId })
    return this.dealViewRaw$.pipe(
      take(1),
      switchMap(dv => this.DealForm.changeDealParty(dv, this.dealForm, DealPartyE.supplier, supplierId)),
      switchMap(() => this.doCalculations()))
  }

  /**
   * Observe buyer change
   *
   * @param {*} newBuyerId
   */
  private onBuyerChange(buyerId: string) {
    if (this.DealForm.preventBuyerChangeIfInvoiceWithReceipt(this.dealViewRaw$, buyerId, this.detailsForm)) {
      return of(undefined)
    }

    if (!buyerId) {
      this.DealForm.cleanBuyerFields(this.detailsForm)
      return this.DealForm.refreshSecondaryCosts(this.dealForm)
    }

    if (isBwiInventory(buyerId)) {
      return this.showDocsCountryConfirmationForBwi(buyerId)
    }

    return this.DealForm.preventBuyerChangeIfNoCredit(this.dealViewRaw$, buyerId, this.detailsForm).pipe(switchMap(shouldCancel => {
      if (shouldCancel) return of(undefined)

      return this.proceedWithBuyerChange(buyerId)
    }))
  }

  /**
   * Proceed with buyer change after all validations
   */
  private proceedWithBuyerChange(buyerId: string, docsCountryCode?: string) {
    const { supplierId } = this.detailsForm.getRawValue()
    this.suggestDealAutocopy({ buyerId, supplierId })
    this.refreshBuyerBalance(buyerId)

    return this.dealViewRaw$.pipe(
      take(1),
      switchMap(dv => this.DealForm.changeDealParty(dv, this.dealForm, DealPartyE.buyer, buyerId, {
        overrideDocsCountry: docsCountryCode
      })),
      switchMap(() => this.doCalculations()))
  }

  /**
   * Proceed with buyer change without autocopy (used when autocopy was already executed)
   */
  private proceedWithBuyerChangeWithoutAutocopy(buyerId: string, docsCountryCode?: string) {
    this.refreshBuyerBalance(buyerId)

    return this.dealViewRaw$.pipe(
      take(1),
      switchMap(dv => this.DealForm.changeDealParty(dv, this.dealForm, DealPartyE.buyer, buyerId, {
        overrideDocsCountry: docsCountryCode
      })),
      switchMap(() => this.doCalculations()))
  }



  private showDocsCountryConfirmationForBwi(buyerId: string) {
    return combineLatest([
      this.accounts$,
      this.countries$
    ]).pipe(
      take(1),
      switchMap(([accounts, countries]) => {
        const bwiAccount = accounts[buyerId]
        const { country: defaultDocsCountry } = getDefaultGeo(bwiAccount)

        // STEP 1: Execute autocopy deal functionality first and wait for completion
        const { supplierId } = this.detailsForm.getRawValue()
        return this.waitForDealAutocopy({ buyerId, supplierId }).pipe(
          switchMap(() => {
            // STEP 2: Only after autocopy modal is closed, show docs country confirmation modal
            return this.docsCountryDialog.showDocsCountryConfirmation(defaultDocsCountry, countries)
          })
        )
      }),
      switchMap(result => {
        if (!result) {
          // User cancelled - revert buyer selection
          this.detailsForm.controls.buyerId.setValue(null)
          return of(undefined)
        }

        const selectedDocsCountry = result.docsCountryCode
        this.detailsForm.controls.docsCountryCode.setValue(selectedDocsCountry)
        this.detailsForm.controls.docsCountryCode.markAsDirty()

        this.onDocsCountryChange()

        this.DealForm.preventBuyerChangeIfNoCredit(this.dealViewRaw$, buyerId, this.detailsForm).pipe(
          switchMap(shouldCancel => {
            if (shouldCancel) return of(undefined)
            return this.proceedWithBuyerChangeWithoutAutocopy(buyerId, selectedDocsCountry)
          }),
          untilComponentDestroyed(this)
        ).subscribe({
          error: (error) => {
            console.error('Error processing buyer change:', error)
          }
        })

        return of(undefined)
      })
    )
  }



  onOriginLocationChange(locationId: DeepReadonly<LocationObject>) {
    for (const productForm of this.productsForm.controls) {
      const ctrl = productForm.controls.supplierIncotermLocationId
      ctrl.setValue(locationId.location_id)
      ctrl.markAsDirty()
    }
    this.refreshSecondaryCosts()
  }

  onDestLocationChange(locationId: DeepReadonly<LocationObject>) {
    for (const productForm of this.productsForm.controls) {
      const ctrl = productForm.controls.buyerIncotermLocationId
      ctrl.setValue(locationId.location_id)
      ctrl.markAsDirty()
    }
    this.refreshSecondaryCosts()
  }

  onOriginCountryChange() {
    this.refreshSecondaryCosts()
  }

  onDocsCountryChange() {
    // Proforma document is mandatory for not-brokerage deals with documents country = Colombia
    const detailsForm = this.detailsForm.getRawValue()
    const ctrl = this.detailsForm.controls.proformaNeeded
    const value = detailsForm.docsCountryCode === 'CO' && detailsForm.dealType !== 'brokerage'
    if (!!ctrl.value !== !!value) {
      ctrl.setValue(value)
      ctrl.markAsDirty()
      if (!value && ctrl.disabled) ctrl.enable()
    }
    this.refreshSecondaryCosts()
  }

  onProductChange() {
    this.refreshSecondaryCosts()
  }

  private setupItemTypeAutoPopulation() {
    this.productsForm.controls.forEach((productForm: DealProductFormGroup) => {
      setupItemSpecPackageAutoPopulation(
        productForm.controls.itemTypeId,
        productForm.controls.packageId,
        this
      )
    })
  }

  onSupplierMeasureChange(index: number, nextMeasure: Measure) {
    const productForm = this.productsForm.at(index)
    const { supplierMeasureId: prevMeasure, buyerMeasureId } = this.productsForm.getRawValue()[index]
    const patch = this.DealFormCalculator.autoconvertSupplierWeight(productForm.getRawValue(), prevMeasure, nextMeasure?.measure_id)
    if (patch) productForm.patchValue(patch)
    if (this.Measures.canConvertMeasureSync(nextMeasure?.measure_id, buyerMeasureId) === false) {
      productForm.patchValue({ buyerMeasureId: nextMeasure?.measure_id })
    }
    this.onSupplierWeightChange(index)
  }

  onBuyerMeasureChange(index: number, nextMeasure: Measure) {
    const productForm = this.productsForm.at(index)
    const patch = this.DealFormCalculator.autoconvertBuyerWeight(productForm.getRawValue(), nextMeasure?.measure_id)
    if (patch) productForm.patchValue(patch)
    this.onBuyerWeightChange(index)
  }

  onSupplierWeightChange(index: number) {
    const productForm = this.productsForm.at(index)
    const { supplierEstWeight, supplierMeasureId, buyerMeasureId, supplierActualWeight, buyerActualWeight } = productForm.getRawValue()
    const buyerEstWeight = round(this.Measures.convert(supplierEstWeight, supplierMeasureId, buyerMeasureId), 2)
    const patch: Partial<DealProductFormValueBase> = { buyerEstWeight }
    if (supplierActualWeight) patch.supplierActualWeight = supplierEstWeight
    if (buyerActualWeight) patch.buyerActualWeight = buyerEstWeight
    productForm.patchValue(patch)
    Object.keys(patch).forEach((key: keyof DealProductFormValueBase) =>
      productForm.controls[key].markAsDirty())
    this.doCalculations().subscribe()
  }

  onBuyerWeightChange(index: number) {
    const productForm = this.productsForm.at(index)
    const { buyerEstWeight, buyerActualWeight } = productForm.getRawValue()
    if (buyerActualWeight) {
      productForm.patchValue({ buyerActualWeight: buyerEstWeight })
      productForm.controls.buyerActualWeight.markAsDirty()
    }
    this.doCalculations().subscribe()
  }

  onSupplierPriceChange(productForm: DealProductFormGroup) {
    const ctrl = productForm.controls.supplierActualPrice
    ctrl.setValue(productForm.getRawValue().supplierEstPrice)
    ctrl.markAsDirty()
    this.doCalculations().subscribe()
  }

  onBuyerPriceChange(productForm: DealProductFormGroup) {
    const ctrl = productForm.controls.buyerActualPrice
    ctrl.setValue(productForm.getRawValue().buyerEstPrice)
    ctrl.markAsDirty()
    this.doCalculations().subscribe()
  }

  onCurrencyChange() {
    this.doCalculations().subscribe()
  }

  showEditSupplierInstructions() {
    combineLatest([this.dealViewRaw$, this.hasSupplierInstructions$]).pipe(take(1)).subscribe(([dv, existing]) =>
      this.NoteForm.showSpecialInstructions(dv, existing, DealPartyE.supplier, SPECIAL_INSTRUCTIONS))
  }

  showEditBuyerInstructions() {
    combineLatest([this.dealViewRaw$, this.hasBuyerInstructions$]).pipe(take(1)).subscribe(([dv, existing]) =>
      this.NoteForm.showSpecialInstructions(dv, existing, DealPartyE.buyer, SPECIAL_INSTRUCTIONS))
  }
  setWoExportDocs({checked}) {
    this.detailsForm.controls.woExportDocs.setValue(!!checked)
  }

  /**
   * Start deal calculations
   *
   * @private
   */
  private refreshSecondaryCosts() {
    this.DealForm.refreshSecondaryCosts(this.dealForm)
      .pipe(switchMap(() => this.doCalculations()))
      .subscribe()
  }

  /**
   * Start deal calculations
   *
   * @private
   */
  private doCalculations() {
    return this.dealViewRaw$.pipe(take(1), switchMap(dto =>
      this.DealFormCalculator.doCalculationsForm(this.dealForm, dto)))
  }

  /**
   * When creating a deal open autocopy suggestion dialog when user changed supplier or buyer
   *
   * @private
   */
  private async suggestDealAutocopy({supplierId, buyerId}: Pick<DealDetailsFormValue, 'supplierId'|'buyerId'>) {
    const deal = await this.dealRaw$.pipe(take(1)).toPromise()
    if (this.dealId || !supplierId || !buyerId || deal.attributes.estimate_id) return // only new deals and only if supplier and buyer are set
    this.DealAutocopyForm.show({ supplierId, buyerId, dealDetails: this.detailsForm.getRawValue() }).subscribe()
  }

  /**
   * Wait for deal autocopy modal to complete before proceeding
   * Returns an Observable that completes when autocopy flow is finished
   */
  private waitForDealAutocopy({supplierId, buyerId}: Pick<DealDetailsFormValue, 'supplierId'|'buyerId'>) {
    return this.dealRaw$.pipe(
      take(1),
      switchMap(deal => {
        // Check if autocopy should be shown (same conditions as suggestDealAutocopy)
        if (this.dealId || !supplierId || !buyerId || deal.attributes.estimate_id) {
          // No autocopy needed, proceed immediately
          return of(null)
        }

        // Show autocopy modal and wait for it to complete
        return this.DealAutocopyForm.show({ supplierId, buyerId, dealDetails: this.detailsForm.getRawValue() })
      })
    )
  }

  private refreshBuyerBalance(buyerId: string) {
    if (!buyerId) return
    this.CreditPoolSvc.getBalance(buyerId).pipe(
      catchError((err: any) => {
        console.error('Unable to fetch buyer balance', err)
        this.toaster.error('Unable to fetch buyer balance', err?.error?.error?.error?.message || err)
        return of(undefined)
      }),
    ).subscribe(balance => { if (balance) this.balance$.next(balance) })
    this.CreditPoolSvc.getFor(buyerId).then(creditPool => this.creditPool$.next(creditPool))
  }
}
